import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useCallback } from 'react';
import { tokenRequest } from './msalConfig';
import { getViewAs } from './viewAs';
import { isInTeams, getTeamsToken } from './teamsAuth';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export const useApi = () => {
  const { instance, accounts } = useMsal();

  const acquireToken = useCallback(async (): Promise<string> => {
    // Inside Microsoft Teams, prefer the native host token (no popup). This is
    // the only reliable path in the iOS Teams webview, where acquireTokenPopup
    // is blocked. The token's audience is the api:// App ID URI, which the
    // backend accepts (JWT_AUDIENCE_TEAMS).
    if (await isInTeams()) {
      try {
        return await getTeamsToken();
      } catch (teamsErr) {
        // Fall through to MSAL below if the host couldn't mint a token.
        console.warn('[useApi] Teams getAuthToken failed, falling back to MSAL', teamsErr);
      }
    }
    const account = accounts[0] || instance.getActiveAccount();
    if (!account) throw new Error('No active account');
    try {
      const result = await instance.acquireTokenSilent({ ...tokenRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup(tokenRequest);
        return result.accessToken;
      }
      throw err;
    }
  }, [instance, accounts]);

  const call = useCallback(
    async <T,>(path: string, options: ApiOptions = {}): Promise<T> => {
      const token = await acquireToken();
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        ...(options.headers as Record<string, string> | undefined),
      };
      if (options.body !== undefined) headers['Content-Type'] = 'application/json';
      const viewAs = getViewAs();
      if (viewAs) headers['X-View-As'] = viewAs;

      const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`API ${res.status}: ${text || res.statusText}`);
      }
      if (res.status === 204) return undefined as unknown as T;
      return (await res.json()) as T;
    },
    [acquireToken]
  );

  return {
    get: <T,>(path: string) => call<T>(path),
    post: <T,>(path: string, body?: unknown) => call<T>(path, { method: 'POST', body }),
    patch: <T,>(path: string, body?: unknown) => call<T>(path, { method: 'PATCH', body }),
    put: <T,>(path: string, body?: unknown) => call<T>(path, { method: 'PUT', body }),
    del: <T,>(path: string) => call<T>(path, { method: 'DELETE' }),
    // For non-JSON requests (e.g. multipart file uploads) where we need to drive fetch ourselves.
    getAccessToken: acquireToken,
    apiBase: API_BASE,
  };
};
